import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import AudioPlayer from "./AudioPlayer";
import AudioSpeechSummary from "./AudioSpeechSummary";
import AudioCheatingAnalysis from "../AudioCheatingAnalysis";
import Tab from "src/components/Common/Tab";
import PdfReportApi from "src/services/apis/pdfReport";
import "./VideoPlayer.scss";

/**
 * Audio-only proctoring review — shown when a position has audio proctoring but no
 * video. Fetches the audio chunks for the selected audio session and the VAD
 * events for the assessment, and renders them on an audio player with speech
 * markers. Mirrors VideoProctoringContainer, minus snapshots/profile.
 *
 * [UNB-2165] The audio-events response also carries `summary` (speech metrics)
 * and `markers` (unexpected-speech moments); both are shown above/below the
 * player, and a marker row can seek the player to that moment.
 */
const AudioProctoringContainer = ({ audioSessionsList, audioProctoringReport }) => {
    const { linkId } = useParams()
    const playerRef = useRef(null)
    const [sessionsList, setSessionsList] = useState([])
    const [currentSession, setCurrentSession] = useState("")
    const [audioChunks, setAudioChunks] = useState([])
    const [audioEvents, setAudioEvents] = useState([])
    const [audioSummary, setAudioSummary] = useState(null)
    const [audioMarkers, setAudioMarkers] = useState([])
    // Index of the marker row currently being played as a segment (null = none).
    const [activeMarkerIndex, setActiveMarkerIndex] = useState(null)
    const [loading, setLoading] = useState(false)

    // Play just that marker's speech region; clicking the active row pauses it.
    const handleMarkerToggle = (index, marker) => {
        if (activeMarkerIndex === index) {
            playerRef.current?.pause()
            setActiveMarkerIndex(null)
            return
        }
        const lengthMs = marker.unexpectedMs ?? marker.durationMs ?? 0
        playerRef.current?.playSegment(marker.seekSec, marker.seekSec + lengthMs / 1000)
        setActiveMarkerIndex(index)
    }

    useEffect(() => {
        if (audioSessionsList?.length > 0) setSessionsList(audioSessionsList)
    }, [audioSessionsList])

    useEffect(() => {
        if (sessionsList?.length > 0 && !currentSession) setCurrentSession(sessionsList[0])
    }, [sessionsList, currentSession])

    // VAD events, speech metrics and unexpected-speech markers for the whole
    // assessment (audio recording clock).
    useEffect(() => {
        if (!linkId) return
        (async () => {
            try {
                const response = await PdfReportApi.getAudioEvents(linkId)
                if (response?.success) {
                    setAudioEvents(response.events || [])
                    setAudioSummary(response.summary || null)
                    setAudioMarkers(response.markers || [])
                }
            } catch (err) { }
        })()
    }, [linkId])

    // Audio chunks for the selected session.
    useEffect(() => {
        (async () => {
            if (!currentSession?.id) return
            setActiveMarkerIndex(null)
            try {
                setLoading(true)
                const response = await PdfReportApi.getSessionVideos(currentSession.id)
                if (response.success) setAudioChunks(response.data || [])
            } catch (err) {
                setAudioChunks([])
            } finally {
                setLoading(false)
            }
        })()
    }, [currentSession])

    return (
        <div className="audio-proctoring-container mt-8 flex flex-col items-center w-full">
            {sessionsList?.length > 1 && (
                <div className="w-full flex justify-start mt-4 mb-12 scroll-x" style={{ minHeight: '40px' }}>
                    <Tab
                        currentKey={currentSession?.id}
                        data={sessionsList.map((session, index) => ({
                            name: `Session ${index + 1}`,
                            key: session?.id,
                        }))}
                        onClick={(sessionId) => {
                            const session = sessionsList?.find((s) => s?.id === sessionId)
                            setCurrentSession(session)
                        }}
                        titleClassName={"text-16"}
                    />
                </div>
            )}
            <div className="w-full">
                <AudioCheatingAnalysis audioProctoringReport={audioProctoringReport} />
            </div>
            <AudioPlayer
                ref={playerRef}
                loading={loading}
                audioChunks={audioChunks}
                audioEvents={audioEvents}
                onSegmentStop={() => setActiveMarkerIndex(null)}
            />
            <AudioSpeechSummary
                summary={audioSummary}
                markers={audioMarkers}
                events={audioEvents}
                audioProctoringReport={audioProctoringReport}
                activeIndex={activeMarkerIndex}
                onToggle={handleMarkerToggle}
            />
        </div>
    )
}

export default AudioProctoringContainer
